import Avatar from "components/core/Avatar";
import type { ComplexNavigationHistoryItemNode } from "components/financials/modern/atoms";
import { ScrollArea, ScrollBar } from "components/ui/ScrollArea";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import { AnimatePresence, motion } from "framer-motion";
import sortedUniqBy from "lodash/sortedUniqBy";
import { EraserIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";

interface IssuerNavigationHistoryStackProps {
  history: ComplexNavigationHistoryItemNode[];
  disabled?: boolean;
  onIssuerClick?: (issuer: ComplexNavigationHistoryItemNode) => void;
  onClearHistory?: () => void;
  children?: ReactNode;
}

export function IssuerNavigationHistoryStack({
  history,
  disabled,
  onIssuerClick,
  onClearHistory,
  children,
}: IssuerNavigationHistoryStackProps) {
  const intl = useIntl();
  const nodes = sortedUniqBy(history, ({ company }) => company._id);
  const formattedConfirmMessage = intl.formatMessage({
    defaultMessage: "Você tem certeza que deseja limpar o histórico?",
    id: "bF8fXU",
  });

  return (
    <ScrollArea className="rounded-3xl" scrollHideDelay={300}>
      <div className="flex items-center gap-2 rounded-3xl bg-slate-300/40 p-2 dark:bg-slate-950 lg:inline-flex">
        {children}
        <AnimatePresence>
          {nodes
            .sort((a, b) => (b.timeStamp || 0) - (a.timeStamp || 0))
            .map((node) => {
              const { company } = node;
              return (
                <motion.div
                  key={company._id}
                  initial={{
                    scale: 0,
                  }}
                  animate={{
                    scale: 1,
                  }}
                  exit={{
                    scale: 0,
                  }}
                  className="relative inline-flex"
                >
                  <button
                    type="button"
                    aria-label={company.name}
                    className={classNames("hover:opacity-80")}
                    disabled={disabled}
                    onClick={() => onIssuerClick?.(node)}
                  >
                    <SimpleTextTooltip content={company.name}>
                      <Avatar
                        src={company.niceLogo?.url || null}
                        hashValue={company._id}
                        alt={company.name}
                        size={42}
                        squircle={true}
                        className={classNames("md:inline-flex")}
                      />
                    </SimpleTextTooltip>
                  </button>
                </motion.div>
              );
            })}
        </AnimatePresence>
        {onClearHistory && history.length >= 3 && (
          <button
            type="button"
            className={classNames(
              "inline-flex size-10 items-center justify-center rounded-xl",
              "bg-stone-600/5 text-stone-600 hover:bg-stone-500 hover:text-white",
              "dark:bg-violet-600/20 dark:text-violet-300 dark:hover:text-white",
            )}
            disabled={disabled}
            onClick={() => {
              // eslint-disable-next-line no-restricted-globals, no-alert
              if (confirm(formattedConfirmMessage)) {
                onClearHistory();
              }
            }}
          >
            <SimpleTextTooltip content={intl.formatMessage({ defaultMessage: "Limpar Histórico", id: "/plnaa" })}>
              <EraserIcon className="size-6" absoluteStrokeWidth={true} strokeWidth={1.5} />
            </SimpleTextTooltip>
          </button>
        )}
      </div>
      <ScrollBar orientation="horizontal" className="bg-transparent" />
    </ScrollArea>
  );
}
